import { Reveal } from "@/components/motion";
import PageHero from "@/components/PageHero";
import CTASection from "@/components/CTASection";
import usePageMeta from "@/hooks/usePageMeta";
import { BRAND, IMAGES, MILESTONES } from "@/data/content";

export default function About() {
  usePageMeta(
    `About — ${BRAND.name}`,
    "The 40-year journey from export-import frontlines to AI strategy and digital development. Experience you can trust, innovation you can use."
  );

  return (
    <div data-testid="about-page">
      <PageHero
        kicker="About Me"
        lines={["Four Decades of Trade.", "One New Chapter."]}
        description="The story of a career built on ships, containers and handshakes — and why it now runs on strategy, software and AI."
        accent="gold"
      />

      {/* STORY + PORTRAIT */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-6xl px-6">
          <div className="grid items-start gap-14 md:grid-cols-12">
            <Reveal className="md:col-span-5">
              <div className="relative">
                <div className="absolute -left-4 -top-4 h-full w-full rounded-2xl border border-gold/40" />
                <div className="relative overflow-hidden rounded-2xl">
                  <img
                    src={IMAGES.portrait}
                    alt={`${BRAND.name} — Founder, ${BRAND.company}`}
                    data-testid="about-portrait"
                    className="aspect-[4/5] w-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#051320]/60 via-transparent to-transparent" />
                </div>
                <p className="mt-4 text-center text-xs uppercase tracking-[0.25em] text-slate-500">
                  {BRAND.name} — Founder, {BRAND.company}
                </p>
              </div>
            </Reveal>
            <div className="md:col-span-7">
              <Reveal>
                <p className="kicker mb-5 text-gold">My Story</p>
                <h2 className="font-serif text-3xl leading-tight text-white md:text-4xl">
                  From the Docks to <span className="italic text-gold">the Digital Age.</span>
                </h2>
                <div className="mt-8 space-y-5 text-sm leading-relaxed text-slate-400 md:text-base">
                  <p>
                    Forty years ago, I started in export-import the way everyone did then — with a
                    telephone, a telex machine, and a willingness to learn every lesson the hard way.
                    I sourced products, negotiated with buyers across cultures, stood at ports
                    watching my containers get loaded, and built a reputation one kept promise at a time.
                  </p>
                  <p>
                    Over the decades, the trade changed around me. Fax became email. Email became
                    instant messaging. Paper bills of lading became digital. And through every wave
                    of change, one thing stayed constant: businesses that adapted early, won.
                  </p>
                  <p>
                    That's why, instead of retiring, I made the most exciting pivot of my career —
                    into AI strategy and digital development. Not because the old ways stopped
                    mattering, but because the old wisdom deserves new tools. Today I help businesses
                    do both: trade globally with four decades of hard-won knowledge, and grow
                    digitally with modern AI and web technology.
                  </p>
                </div>
              </Reveal>
              <Reveal delay={0.15}>
                <div className="mt-10 rounded-2xl border-l-2 border-gold bg-[#0A2540] p-8">
                  <p className="kicker mb-4 text-gold">My Mission</p>
                  <p className="font-serif text-xl italic leading-relaxed text-slate-200 md:text-2xl">
                    "To put forty years of global trade wisdom — and the power of modern AI — into
                    the hands of businesses that deserve to grow beyond borders."
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="py-20 md:py-28" data-testid="about-timeline">
        <div className="mx-auto max-w-4xl px-6">
          <Reveal className="mb-16 text-center">
            <p className="kicker mb-5 text-gold">The Journey</p>
            <h2 className="font-serif text-3xl leading-tight text-white md:text-4xl">
              Milestones Along <span className="italic text-gold">the Way.</span>
            </h2>
          </Reveal>
          <div className="relative border-l border-[#1E3A5F] pl-10 md:pl-14">
            {MILESTONES.map((m, i) => (
              <Reveal key={m.year} delay={i * 0.08} className="relative pb-14 last:pb-0">
                <span className="absolute -left-[46px] top-1.5 h-3 w-3 rounded-full border-2 border-gold bg-[#051320] md:-left-[62px]" />
                <span className="font-serif text-3xl text-gold/50 md:text-4xl">{m.year}</span>
                <h3 className="mt-2 font-serif text-2xl text-white">{m.title}</h3>
                <p className="mt-3 max-w-xl text-sm leading-relaxed text-slate-400 md:text-base">{m.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        kicker="Let's Write the Next Chapter"
        title="Your business could be part of this story."
        subtitle="Whether it's your first shipment or your first website — let's talk about what's possible."
      />
    </div>
  );
}
